import { useCallback, useEffect, useRef, useState } from "react";
import BoBNotesAPI from "../../../api/BoBNotesAPI";

/**
 * Notes for one BoB client. `clientKey` is the row's `anchor_key` (HealthSherpa ID, or the
 * workbook's temp identifier): every cutoff's row of a client carries it, regular or backdated,
 * so every one of them shows the same list. The row's own `id` changes each cutoff.
 *
 * Runs as soon as a row is opened (the header badge needs the count), so it has to cope with
 * the user clicking through rows quickly: switching rows clears the previous client's notes
 * at once, and a response for a row that is no longer open is dropped.
 */
export default function useBoBNotes(clientKey, period = null) {
	const [notes, setNotes] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const currentId = useRef(clientKey);

	const refresh = useCallback(async () => {
		currentId.current = clientKey;

		if (!clientKey) {
			setNotes([]);
			setLoading(false);
			return;
		}

		setLoading(true);
		setError("");

		try {
			const data = await BoBNotesAPI.getNotes(clientKey);
			if (currentId.current !== clientKey) return;
			setNotes(Array.isArray(data.notes) ? data.notes : []);
		} catch (e) {
			if (currentId.current !== clientKey) return;
			setError(e.message);
		} finally {
			if (currentId.current === clientKey) setLoading(false);
		}
	}, [clientKey]);

	useEffect(() => {
		setNotes([]);
		setError("");
		refresh();
	}, [refresh]);

	const createNote = useCallback(
		async (html) => {
			// Filed under the cutoff being viewed, so a note says which month it was written in.
			await BoBNotesAPI.createNote(clientKey, html, period);
			await refresh();
		},
		[clientKey, period, refresh]
	);

	const updateNote = useCallback(
		async (id, html) => {
			await BoBNotesAPI.updateNote(id, html);
			await refresh();
		},
		[refresh]
	);

	const deleteNote = useCallback(async (id) => {
		await BoBNotesAPI.deleteNote(id);
		setNotes((prev) => prev.filter((n) => n.id !== id));
	}, []);

	return { notes, loading, error, createNote, updateNote, deleteNote };
}
